/* eslint-disable no-console */
import { GameRoomState, GameState } from "@presidents/game-engine";
import { InPlay } from "@presidents/game-engine/src/game";
import { PlayerId } from "@presidents/game-engine/src/types";
import { User } from "../context/UserContext";
import arrayDifference from "./array-difference";
import { diffHand } from "./compare-hand";
import { createPlayerMap } from "./create-player-map";

export const indefiniteArticle = (count: number, thing: string) => {
  const prefix = numCardsString[count] || "";
  if (prefix === "a") {
    if (thing === "8" || thing === "ace") {
      return "an";
    }
  }
  return prefix;
};

const numCardsString = [
  "nothing!?",
  "a",
  "two",
  "three",
  "four",
  "five",
  "six",
  "seven",
  "eight"
] as const;

export function getCardsPlayedString(trick: InPlay) {
  if (trick.__outcome?.isWild && trick.__original_turn?.type === "trick") {
    return trick.__original_turn.cards.map((c) => c.value).join(", ");
  } else if (trick.__outcome?.isSpecial) {
    return trick.__outcome?.outcome === "equal"
      ? "ANOTHER SIXTY NINE!?!! 😱"
      : "SIXTY NINE!!!";
  } else if (trick.__outcome?.isClearCard && trick.__outcome?.winsTrick) {
    return `an INSTANT WIN ${trick.cards[0].value}! 💥`;
  }
  const numCards = trick.cards.length;
  const prefix = indefiniteArticle(numCards, "" + trick.cards[0].value);
  let suffix = numCards > 1 ? "s" : "";
  if (trick.__outcome?.isLargerSet) {
    suffix += " (larger set beats 💪)";
  }
  return `${prefix} ${trick.cards[0].value}${suffix}`;
}

export function createStateChangeMessages(
  user: User,
  state: GameRoomState | null | undefined,
  nextState: GameRoomState
): string[] | undefined {
  if (!nextState) {
    return;
  }

  const curr = state?.currentGame?.state;
  const next = nextState.currentGame?.state;

  const messages: string[] = [];
  const playersMap = createPlayerMap(state?.players);
  const nextPlayersMap = createPlayerMap(nextState.players);
  const numTurns = curr?.turns.length;
  const nextNumTurns = next?.turns.length;
  /**
   * A Turn has happened
   */
  if (nextNumTurns !== numTurns) {
    const turnsTaken = next?.lastTurn;
    if (turnsTaken) {
      turnsTaken.forEach((turn) => {
        const wasMe = turn.player === user?.uid;
        const whoString = wasMe
          ? "You"
          : nextPlayersMap[turn.player].name || "??";
        messages.push(
          turn.type === "pass"
            ? `${turn.reason === "player" ? "😳" : "😵 "}${whoString} ${
                turn.reason === "player" ? "passed" : "missed a go!"
              }!`
            : `${whoString} played ${getCardsPlayedString(turn as InPlay)}`
        );
      });
    }
  }

  const playersWhoJoined = arrayDifference(
    Object.keys(nextPlayersMap),
    Object.keys(playersMap)
  );

  const playersWhoLeft = arrayDifference(
    Object.keys(playersMap),
    Object.keys(nextPlayersMap)
  );

  if (playersWhoJoined.length) {
    playersWhoJoined.forEach((playerId) => {
      if (playerId !== user.uid) {
        messages.push(
          `👋 ${nextPlayersMap[playerId]?.name ?? "Player"} joined!`
        );
      }
    });
  }
  if (playersWhoLeft.length) {
    playersWhoLeft.forEach((playerId) => {
      if (playerId !== user.uid) {
        messages.push(`🏃💨 ${playersMap[playerId]?.name ?? "Player"} left!`);
      }
    });
  }

  /**
   * Player went out!
   */
  const handStats = curr?.handStats;
  if (handStats && next?.handStats) {
    let whoWentOut: undefined | PlayerId = undefined;
    for (const [playerId, { isOut }] of Object.entries(next.handStats)) {
      if (isOut && !handStats[playerId].isOut) {
        whoWentOut = playerId;
        break;
      }
    }
    if (whoWentOut && !playersWhoLeft.includes(whoWentOut)) {
      const wasMe = whoWentOut === user?.uid;
      const whoString = wasMe ? "You" : playersMap[whoWentOut]?.name || "??";
      if (curr.out.length === 0) {
        messages.push(
          `🎉🎉🎉🥳 ${whoString} ${wasMe ? "are" : "is"} PRESIDENT!`
        );
      } else {
        messages.push(`🤘 ${whoString} went out!`);
      }
    }
  }

  /**
   * The game state has changed!
   */
  if (curr?.state !== next?.state || curr?.round !== next?.round) {
    if (next?.state === GameState.START_ROUND && next.round > 0) {
      const whoWon = next.whosNext!;
      const wasMe = whoWon === user?.uid;
      const whoString = wasMe ? "You" : nextPlayersMap[whoWon]?.name || "??";
      const lastTurn = next.lastTurn.pop();
      if (lastTurn && lastTurn.__outcome?.isRevolution) {
        messages.push(`👏 ${whoString} played a REVOLUTION! 👏`);
      }
      messages.push(`🎉 ${whoString} won the round!`);
    }
    if (
      curr?.state === GameState.SWAP &&
      next?.state === GameState.START_ROUND
    ) {
      const added = diffHand(next?.myHand, curr?.myHand);
      const removed = diffHand(curr?.myHand, next?.myHand);
      if (added?.length && removed?.length) {
        const deltTo = added.map((c) => c.deltTo)[0];
        const whoString = (deltTo && nextPlayersMap[deltTo]?.name) || "??";
        messages.push(
          `You swapped a ${removed.map((c) => c.value)} for a ${added.map(
            (c) => c.value
          )} with ${whoString}`
        );
      }
    }
  }

  if (messages.length) {
    return messages;
  }
}

export default function stateChangeMessage(
  user: User,
  state: GameRoomState | null | undefined,
  nextState: GameRoomState
): string[] | undefined {
  try {
    const messages = createStateChangeMessages(user, state, nextState);
    console.groupCollapsed("State change message");
    console.log({ state, nextState });
    messages?.forEach((msg) => {
      console.info(msg);
    });
    console.groupEnd();
    return messages;
  } catch (err: unknown) {
    console.error("State change message error!", err);
    console.error({ err, user, state, nextState });
  }
}
